import React, { useState } from 'react';
import { BookOpen, Code } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';

const APIDocs = () => {
  const navigate = useNavigate();
  const [showCopyNotification, setShowCopyNotification] = useState({});

  // SEO
  useSEO({
    title: 'API Documentation - Stick Audio',
    description: 'Comprehensive API documentation for Stick Audio. Learn how to integrate text-to-speech into your applications with code examples in cURL, Node.js, and Python.',
    keywords: 'API documentation, REST API, integration guide, code examples',
  });

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setShowCopyNotification({ [label]: true });
      setTimeout(() => {
        setShowCopyNotification({});
      }, 2000);
    }).catch(() => {
      console.error('Failed to copy to clipboard');
    });
  };

  const apiHost = 'https://api.stick.audio';

  // Code examples
  const curlExample = `# Step 1: Start generation (returns job_id immediately)
JOB_ID=$(curl -X POST ${apiHost}/api/v1/tts/generate-with-api \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "text": "Hello world",
    "voice": "Asher",
    "temperature": 0.8
  }' | jq -r '.job_id')

# Step 2: Poll for completion (check every 5 seconds)
while true; do
  STATUS=$(curl -s ${apiHost}/api/v1/tts/jobs/$JOB_ID \\
    -H "X-API-Key: YOUR_API_KEY" | jq -r '.status')

  if [ "$STATUS" = "completed" ]; then
    curl -s ${apiHost}/api/v1/tts/jobs/$JOB_ID \\
      -H "X-API-Key: YOUR_API_KEY" | jq '.audio_url'
    break
  elif [ "$STATUS" = "failed" ]; then
    echo "Generation failed"
    break
  fi

  sleep 5
done`;

  const nodeExample = `const axios = require('axios');

async function generateSpeech() {
  try {
    // Step 1: Start generation (returns job_id immediately)
    const startResponse = await axios.post(
      '${apiHost}/api/v1/tts/generate-with-api',
      {
        text: 'Hello world',
        voice: 'Asher',
        temperature: 0.8
      },
      {
        headers: {
          'X-API-Key': 'YOUR_API_KEY',
          'Content-Type': 'application/json'
        }
      }
    );

    const jobId = startResponse.data.job_id;
    console.log('Job created:', jobId);

    // Step 2: Poll for completion
    while (true) {
      const statusResponse = await axios.get(
        \`${apiHost}/api/v1/tts/jobs/\${jobId}\`,
        {
          headers: { 'X-API-Key': 'YOUR_API_KEY' }
        }
      );

      const { status, progress, audio_url, error_message } = statusResponse.data;
      console.log(\`Status: \${status} - Progress: \${progress}%\`);

      if (status === 'completed') {
        console.log('Audio URL:', audio_url);
        break;
      } else if (status === 'failed') {
        console.error('Generation failed:', error_message);
        break;
      }

      // Wait 5 seconds before next poll
      await new Promise(resolve => setTimeout(resolve, 5000));
    }
  } catch (error) {
    console.error('Error:', error.response?.data || error.message);
  }
}

generateSpeech();`;

  const pythonExample = `import requests
import time

try:
    # Step 1: Start generation (returns job_id immediately)
    response = requests.post(
        '${apiHost}/api/v1/tts/generate-with-api',
        json={
            'text': 'Hello world',
            'voice': 'Asher',
            'temperature': 0.8
        },
        headers={
            'X-API-Key': 'YOUR_API_KEY',
            'Content-Type': 'application/json'
        }
    )
    response.raise_for_status()

    job_id = response.json()['job_id']
    print(f'Job created: {job_id}')

    # Step 2: Poll for completion
    while True:
        status_response = requests.get(
            f'${apiHost}/api/v1/tts/jobs/{job_id}',
            headers={'X-API-Key': 'YOUR_API_KEY'}
        )
        status_response.raise_for_status()

        data = status_response.json()
        status = data['status']
        progress = data.get('progress', 0)

        print(f'Status: {status} - Progress: {progress}%')

        if status == 'completed':
            audio_url = data['audio_url']
            print(f'Audio URL: {audio_url}')
            break
        elif status == 'failed':
            error = data.get('error_message', 'Unknown error')
            print(f'Generation failed: {error}')
            break

        # Wait 5 seconds before next poll
        time.sleep(5)

except requests.exceptions.RequestException as e:
    print(f'Error: {e.response.json() if hasattr(e, "response") else str(e)}')`;

  return (
    <div className="policy-page-container">
      {/* Header */}
      <header className="policy-page-header">
        <div className="policy-header-content">
          <h1>API Documentation</h1>
          <button className="btn-home" onClick={() => navigate('/')}>
            🏠 HOME
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="content-wrapper">
        <div className="content-inner">
          <div className="card-container">
            {/* Header */}
            <div style={{ marginBottom: '32px' }}>
              <h2 className="section-title mb-2">
                <BookOpen size={20} strokeWidth={2} style={{ marginRight: '8px', display: 'inline-block' }} />
                API Documentation
              </h2>
              <p className="subtitle">Comprehensive guide to integrate Stick Audio TTS into your applications</p>
            </div>

            {/* Overview Section */}
            <div style={{ marginBottom: '32px', paddingBottom: '32px', borderBottom: '1px solid var(--border-color)' }}>
              <h3 className="section-title mb-2" style={{ fontSize: '16px' }}>
                Overview
              </h3>
              <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                Stick Audio provides a REST API for generating high-quality text-to-speech audio.
                Convert text to natural-sounding speech with multiple AI voices and customization options.
              </p>
              <div style={{ background: 'rgba(22, 23, 29, 0.6)', border: '1.5px solid var(--border-color)', borderRadius: '8px', padding: '16px', marginTop: '16px' }}>
                <p style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>
                  <strong>Base URL:</strong> <code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 6px', borderRadius: '4px' }}>https://api.stick.audio/api/v1</code>
                </p>
                <p style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>
                  <strong>Response Format:</strong> JSON
                </p>
                <p style={{ margin: '0', color: 'var(--text-secondary)', fontSize: '13px' }}>
                  💡 Tip: All responses follow standard HTTP status codes and include detailed error messages.
                </p>
              </div>
            </div>

            {/* Authentication Section */}
            <div style={{ marginBottom: '32px', paddingBottom: '32px', borderBottom: '1px solid var(--border-color)' }}>
              <h3 className="section-title mb-2" style={{ fontSize: '16px' }}>
                Authentication
              </h3>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                The API uses API Keys for authentication. Include your key in the request header:
              </p>
              <div className="api-example-box">
                <div className="api-example-header">
                  <span className="api-example-label">Header Format</span>
                  <button
                    className="btn-copy"
                    onClick={() => copyToClipboard('X-API-Key: YOUR_API_KEY', 'auth')}
                  >
                    {showCopyNotification.auth ? '✓ Copied!' : 'Copy'}
                  </button>
                </div>
                <pre className="api-example-code">{`X-API-Key: YOUR_API_KEY`}</pre>
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginTop: '16px' }}>
                Get your API key by signing up and creating one in your dashboard.
              </p>
            </div>

            {/* Code Examples Section */}
            <div style={{ marginBottom: '32px', paddingBottom: '32px', borderBottom: '1px solid var(--border-color)' }}>
              <h3 className="section-title mb-2" style={{ fontSize: '16px' }}>
                <Code size={18} strokeWidth={2} style={{ marginRight: '8px', display: 'inline-block' }} />
                Code Examples
              </h3>
              <p className="helper-text" style={{ marginBottom: '24px' }}>
                Here are examples of how to call the API using different languages:
              </p>

              <div className="api-examples-grid">
                {/* cURL Example */}
                <div className="api-example-box">
                  <div className="api-example-header">
                    <span className="api-example-label">cURL</span>
                    <button
                      className="btn-copy"
                      onClick={() => copyToClipboard(curlExample, 'curl')}
                    >
                      {showCopyNotification.curl ? '✓ Copied!' : 'Copy'}
                    </button>
                  </div>
                  <pre className="api-example-code">{curlExample}</pre>
                </div>

                {/* Node.js Example */}
                <div className="api-example-box">
                  <div className="api-example-header">
                    <span className="api-example-label">Node.js</span>
                    <button
                      className="btn-copy"
                      onClick={() => copyToClipboard(nodeExample, 'node')}
                    >
                      {showCopyNotification.node ? '✓ Copied!' : 'Copy'}
                    </button>
                  </div>
                  <pre className="api-example-code">{nodeExample}</pre>
                </div>

                {/* Python Example */}
                <div className="api-example-box">
                  <div className="api-example-header">
                    <span className="api-example-label">Python</span>
                    <button
                      className="btn-copy"
                      onClick={() => copyToClipboard(pythonExample, 'python')}
                    >
                      {showCopyNotification.python ? '✓ Copied!' : 'Copy'}
                    </button>
                  </div>
                  <pre className="api-example-code">{pythonExample}</pre>
                </div>
              </div>
            </div>

            {/* Request Parameters Section */}
            <div style={{ marginBottom: '32px', paddingBottom: '32px', borderBottom: '1px solid var(--border-color)' }}>
              <h3 className="section-title mb-2" style={{ fontSize: '16px' }}>
                Request Parameters
              </h3>
              <p className="helper-text" style={{ marginBottom: '16px' }}>
                The endpoint <code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 6px', borderRadius: '4px' }}>POST /api/v1/tts/generate-with-api</code> accepts the following parameters:
              </p>
              <div className="api-response-table-container">
                <table className="api-response-table">
                  <thead>
                    <tr style={{ borderBottom: '1.5px solid var(--border-color)' }}>
                      <th style={{ textAlign: 'left', padding: '12px 8px', color: 'var(--primary-color)', fontWeight: '600' }}>Parameter</th>
                      <th style={{ textAlign: 'left', padding: '12px 8px', color: 'var(--primary-color)', fontWeight: '600' }}>Type</th>
                      <th style={{ textAlign: 'left', padding: '12px 8px', color: 'var(--primary-color)', fontWeight: '600' }}>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '10px 8px', color: 'var(--text-primary)' }} data-label="Parameter"><code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 4px', borderRadius: '3px' }}>text</code></td>
                      <td style={{ padding: '10px 8px', color: 'var(--text-secondary)' }} data-label="Type">string</td>
                      <td style={{ padding: '10px 8px', color: 'var(--text-secondary)' }} data-label="Description">Text to convert (max 20,000 characters)</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '10px 8px', color: 'var(--text-primary)' }} data-label="Parameter"><code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 4px', borderRadius: '3px' }}>voice</code></td>
                      <td style={{ padding: '10px 8px', color: 'var(--text-secondary)' }} data-label="Type">string</td>
                      <td style={{ padding: '10px 8px', color: 'var(--text-secondary)' }} data-label="Description">Voice name (see Available Voices)</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px 8px', color: 'var(--text-primary)' }} data-label="Parameter"><code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 4px', borderRadius: '3px' }}>temperature</code></td>
                      <td style={{ padding: '10px 8px', color: 'var(--text-secondary)' }} data-label="Type">number</td>
                      <td style={{ padding: '10px 8px', color: 'var(--text-secondary)' }} data-label="Description">Voice expressiveness (0.1 - 1.0, default: 0.8)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Response Structure Section */}
            <div style={{ marginBottom: '32px', paddingBottom: '32px', borderBottom: '1px solid var(--border-color)' }}>
              <h3 className="section-title mb-2" style={{ fontSize: '16px' }}>
                Response Structure
              </h3>
              <p className="helper-text" style={{ marginBottom: '16px' }}>
                The API uses async job processing. First call returns job_id, then poll for status:
              </p>

              {/* Job Creation Response */}
              <div className="api-example-box">
                <div className="api-example-header">
                  <span className="api-example-label">1. Job Creation Response (200)</span>
                  <button
                    className="btn-copy"
                    onClick={() => {
                      const responseExample = JSON.stringify({
                        job_id: "1b803470-a506-4f00-ab0f-3a3362137819",
                        status: "pending",
                        message: "Job created successfully. Use job_id to poll for status."
                      }, null, 2);
                      copyToClipboard(responseExample, 'response');
                    }}
                  >
                    {showCopyNotification.response ? '✓ Copied!' : 'Copy'}
                  </button>
                </div>
                <pre className="api-example-code">{JSON.stringify({
                  job_id: "1b803470-a506-4f00-ab0f-3a3362137819",
                  status: "pending",
                  message: "Job created successfully. Use job_id to poll for status."
                }, null, 2)}</pre>
              </div>

              {/* Job Status - Completed */}
              <div className="api-example-box" style={{ marginTop: '16px' }}>
                <div className="api-example-header">
                  <span className="api-example-label">2. Job Status - Completed (200)</span>
                  <button
                    className="btn-copy"
                    onClick={() => {
                      const responseExample = JSON.stringify({
                        job_id: "1b803470-a506-4f00-ab0f-3a3362137819",
                        status: "completed",
                        progress: 100,
                        audio_url: "https://pub-xxx.r2.dev/audio/user-id/tts_xxx.wav",
                        file_name: "tts_20260207_071650_xxx.wav",
                        error_message: null,
                        created_at: "2026-02-07T07:14:48Z",
                        completed_at: "2026-02-07T07:16:54Z"
                      }, null, 2);
                      copyToClipboard(responseExample, 'completed');
                    }}
                  >
                    {showCopyNotification.completed ? '✓ Copied!' : 'Copy'}
                  </button>
                </div>
                <pre className="api-example-code">{JSON.stringify({
                  job_id: "1b803470-a506-4f00-ab0f-3a3362137819",
                  status: "completed",
                  progress: 100,
                  audio_url: "https://pub-xxx.r2.dev/audio/user-id/tts_xxx.wav",
                  file_name: "tts_20260207_071650_xxx.wav",
                  error_message: null,
                  created_at: "2026-02-07T07:14:48Z",
                  completed_at: "2026-02-07T07:16:54Z"
                }, null, 2)}</pre>
              </div>

              <div className="api-response-table-container" style={{ marginTop: '16px' }}>
                <table className="api-response-table">
                  <thead>
                    <tr style={{ borderBottom: '1.5px solid var(--border-color)' }}>
                      <th style={{ textAlign: 'left', padding: '12px 8px', color: 'var(--primary-color)', fontWeight: '600' }}>Field</th>
                      <th style={{ textAlign: 'left', padding: '12px 8px', color: 'var(--primary-color)', fontWeight: '600' }}>Type</th>
                      <th style={{ textAlign: 'left', padding: '12px 8px', color: 'var(--primary-color)', fontWeight: '600' }}>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '10px 8px', color: 'var(--text-primary)' }} data-label="Field"><code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 4px', borderRadius: '3px' }}>job_id</code></td>
                      <td style={{ padding: '10px 8px', color: 'var(--text-secondary)' }} data-label="Type">string (UUID)</td>
                      <td style={{ padding: '10px 8px', color: 'var(--text-secondary)' }} data-label="Description">Unique job identifier for polling status</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '10px 8px', color: 'var(--text-primary)' }} data-label="Field"><code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 4px', borderRadius: '3px' }}>status</code></td>
                      <td style={{ padding: '10px 8px', color: 'var(--text-secondary)' }} data-label="Type">string</td>
                      <td style={{ padding: '10px 8px', color: 'var(--text-secondary)' }} data-label="Description">pending, processing, completed, or failed</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '10px 8px', color: 'var(--text-primary)' }} data-label="Field"><code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 4px', borderRadius: '3px' }}>progress</code></td>
                      <td style={{ padding: '10px 8px', color: 'var(--text-secondary)' }} data-label="Type">integer</td>
                      <td style={{ padding: '10px 8px', color: 'var(--text-secondary)' }} data-label="Description">Progress percentage (0-100)</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '10px 8px', color: 'var(--text-primary)' }} data-label="Field"><code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 4px', borderRadius: '3px' }}>audio_url</code></td>
                      <td style={{ padding: '10px 8px', color: 'var(--text-secondary)' }} data-label="Type">string | null</td>
                      <td style={{ padding: '10px 8px', color: 'var(--text-secondary)' }} data-label="Description">Public URL when status is completed</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '10px 8px', color: 'var(--text-primary)' }} data-label="Field"><code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 4px', borderRadius: '3px' }}>file_name</code></td>
                      <td style={{ padding: '10px 8px', color: 'var(--text-secondary)' }} data-label="Type">string | null</td>
                      <td style={{ padding: '10px 8px', color: 'var(--text-secondary)' }} data-label="Description">Audio file name when completed</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px 8px', color: 'var(--text-primary)' }} data-label="Field"><code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 4px', borderRadius: '3px' }}>error_message</code></td>
                      <td style={{ padding: '10px 8px', color: 'var(--text-secondary)' }} data-label="Type">string | null</td>
                      <td style={{ padding: '10px 8px', color: 'var(--text-secondary)' }} data-label="Description">Error details when status is failed</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Error Handling Section */}
            <div style={{ marginBottom: '32px' }}>
              <h3 className="section-title mb-2" style={{ fontSize: '16px' }}>
                Error Handling
              </h3>
              <p className="helper-text" style={{ marginBottom: '24px' }}>
                Common error responses and their meanings:
              </p>

              <div className="api-error-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                {/* 401 Error */}
                <div className="api-example-box">
                  <div className="api-example-header">
                    <span className="api-example-label">401 - Unauthorized</span>
                  </div>
                  <pre className="api-example-code">{JSON.stringify({
                    detail: "Invalid or expired API key"
                  }, null, 2)}</pre>
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '8px 0 0 0' }}>Missing or invalid X-API-Key header</p>
                </div>

                {/* 402 Error */}
                <div className="api-example-box">
                  <div className="api-example-header">
                    <span className="api-example-label">402 - Payment Required</span>
                  </div>
                  <pre className="api-example-code">{JSON.stringify({
                    detail: "Insufficient credits. Required: 150, Available: 50"
                  }, null, 2)}</pre>
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '8px 0 0 0' }}>Not enough credits to generate audio</p>
                </div>

                {/* 400 Error */}
                <div className="api-example-box">
                  <div className="api-example-header">
                    <span className="api-example-label">400 - Bad Request</span>
                  </div>
                  <pre className="api-example-code">{JSON.stringify({
                    detail: "Invalid parameters provided"
                  }, null, 2)}</pre>
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '8px 0 0 0' }}>Invalid text, voice, or temperature parameter</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default APIDocs;
